import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { buttonClasses, inputAdornmentClasses, inputLabelClasses, styled } from '@mui/material';
import classNames from 'classnames';

import { TextField } from '@components/base';
import { IndexName } from '@enums/IndexName';
import { SearchContext } from '@enums/SearchContext';
import useFilterDefsQuery from '@hooks/queryHooks/useFilterDefsQuery';
import { findPropertyLocInFilterDefs } from '@utils/filterUtils';

import GlobalSearchFieldDropDown from './GlobalSearchFieldDropDown';
import { GlobalSearchTextFieldProps } from './utils';

const extendedSearchFieldClass = 'has-focus';

const StyledTextField = styled(TextField)`
  width: 200px;
  transition: 150ms;

  .${inputAdornmentClasses.root} .${buttonClasses.root} {
    text-transform: none;
    padding-right: ${({ theme }) => theme.spacing(0.25)};

    .${buttonClasses.endIcon} {
      margin: 0;
    }
  }

  .${inputLabelClasses.asterisk} {
    display: none;
  }

  &.${extendedSearchFieldClass} {
    width: 420px;
  }
`;

const GlobalSearchTextField = forwardRef<HTMLInputElement, GlobalSearchTextFieldProps>(
  (
    {
      hasFocus,
      queryText,
      searchContext,
      setElementFocus,
      setQueryText,
      setSearchContext,
      ...rest
    },
    ref
  ) => {
    const { t } = useTranslation();
    const { data: filterDefs } = useFilterDefsQuery(IndexName.Assets);

    const getFieldName = () => {
      switch (searchContext) {
        case SearchContext.PLATFORM_TITLE_ID:
        case SearchContext.PROVIDER_TITLE_ID:
          if (filterDefs) return findPropertyLocInFilterDefs(filterDefs, searchContext).join('/');
          break;
        default:
          return searchContext.toString();
      }
    };

    return (
      <StyledTextField
        {...rest}
        className={classNames({ [extendedSearchFieldClass]: hasFocus })}
        inputRef={ref}
        label={t('search')}
        name={getFieldName()}
        onChange={({ currentTarget: { value } }) => setQueryText(value)}
        placeholder={t('search.context.hint', { context: searchContext })}
        required
        slotProps={{
          ...rest.slotProps,
          input: {
            ...rest.slotProps?.input,
            endAdornment: (
              <GlobalSearchFieldDropDown
                hasFocus={hasFocus}
                searchContext={searchContext}
                setElementFocus={setElementFocus}
                setSearchContext={setSearchContext}
              />
            ),
          },
        }}
        value={queryText}
      />
    );
  }
);

export default GlobalSearchTextField;
